import React, { useState, useEffect } from 'react'
import { renderDateTime } from '../../../utils/dateFormatter'
import { getFileSize } from '../../../utils/helpers'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'
import { Col, Form, FormGroup, ModalBody, Row } from 'reactstrap'
import { Extension, Version } from '../types'
import './DataSetsDisplay.css'
import ICSDataGrid from '../../../components/ICSDataGrid/ICSDataGrid'
import { ICSModal } from '../../../components'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { createDataset, setEntityError } from '../../../redux/slices'
import { DisplayErrorState, AppDispatch } from '../../../services/lib'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import { DefaultState } from '../../../types'
import { RequestErrorResponse } from '../../../services/lib/types'

const defaultJoiSchema = {
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`
  }),
  description: Joi.string().min(0).max(255).allow('')
}

const schema = Joi.object(defaultJoiSchema)

function DataSetsDisplay({
  resultJson,
  setResultJson,
  dynamic,
  extensions,
  resultJsonErrors
}: {
  resultJson
  setResultJson
  dynamic: boolean
  extensions
  resultJsonErrors: DisplayErrorState
}) {
  let extensionsToShow = extensions.filter((e: Extension) => e.selected)
  if (extensionsToShow.length === 0) extensionsToShow = extensions
  const [selectedTotal, setSelectedTotal] = useState<{
    count: number
    size: number
    selectedFiles: Version[] | never[]
  }>({
    count: 0,
    size: 0,
    selectedFiles: []
  })

  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const datasetErrors = useSelector(
    (state: DefaultState) => state.datasets.error
  )
  const [hidden, setHidden] = useState<boolean>(false)

  const {
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    control,
    setError
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema)
  })

  useEffect(() => {
    if (datasetErrors) {
      datasetErrors.fields.forEach(async (errorObj) =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message
        })
      )
    }
  }, [datasetErrors, setError])

  const toggleCreateModal = () => {
    if (hidden)
      dispatch(
        setEntityError({
          sliceName: 'datasets',
          entityName: 'Dataset',
          error: null
        })
      )
    setHidden(hidden ? false : true)
  }

  function updateTotal() {
    let count = 0
    let sizeTotal = 0
    const selectedFiles: Version[] = []
    resultJson.forEach((result) => {
      result.versions.forEach((version: Version) => {
        if (version.selected) {
          count++
          sizeTotal += version.size
          selectedFiles.push(version)
        }
      })
    })
    setSelectedTotal({
      count,
      size: sizeTotal,
      selectedFiles
    })
  }

  function updateSelectedForAll(extension) {
    extension.active = !extension.active
    resultJson.forEach((result) => {
      result.versions.forEach((version: Version) => {
        if (version.extension === extension.name) {
          version.selected = extension.active
        }
      })
    })
    setResultJson([...resultJson])
    updateTotal()
  }

  function updateSelectedForOne(item) {
    item.selected = !item.selected
    setResultJson([...resultJson])
    updateTotal()
  }

  function renderDataType(data) {
    return <div className={'flexxer'}>
      {data.value.map((item, i: number) => {
        return (
          <button
            className={`data-type-button ${item.selected ? 'active' : ''}`}
            onClick={() => updateSelectedForOne(item)}
            key={`${item.id}-${i}`}
          >
            {item.extension}
            <br />
            {getFileSize(item.size)}
          </button>
        )
      })}
    </ div>
  }

  const onSubmit = async (formValues) => {
    const { description, name } = formValues

    const selectedFiles = selectedTotal.selectedFiles.filter((file) => {
      return file.selected
    })

    const uploadFileIds = selectedFiles.map((file) => {
      return file.id
    })

    const createdDataset = await dispatch(
      createDataset({
        name: name,
        description: description,
        type: dynamic ? 'dynamic' : 'static',
        filters: [],
        upload_file_version_id: uploadFileIds
      })
    )

    if (createdDataset.type.split('/').pop() === 'fulfilled') {
      navigate('/ui2/datasets')
    } else {
      const verifyFields = await hasInvalidField(
        Object.keys(defaultJoiSchema),
        (createdDataset.payload as RequestErrorResponse).fields.map(
          (fieldObj: { field: string; message: string }) => fieldObj.field
        )
      )

      if (verifyFields.isInvalid) {
        dispatch(
          setEntityError({
            sliceName: 'datasets',
            entityName: 'Dataset',
            error: {
              code: 'error',
              message: invalidFieldsMessage,
              fields: verifyFields.fields
            }
          })
        )
      }
    }
  }

  const createDataSetModal = (
    <ICSModal
      visible={hidden}
      onHiding={() => {
        setHidden(false)
        dispatch(
          setEntityError({
            sliceName: 'datasets',
            entityName: 'Dataset',
            error: null
          })
        )
      }}
      dragEnabled={false}
      closeOnOutsideClick={true}
      title="Create Data Set"
    >
      <ModalBody>
        <Row>
          <Col xs={{ size: 12 }}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <Row>
                  <Col>
                    {'Creating data sets with selected files count: ' +
                      selectedTotal.count}
                    <br />
                    {'Total size: ' + getFileSize(selectedTotal.size)}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {datasetErrors?.code === 'error' ? (
                      <ICSErrorCard
                        code={datasetErrors.code}
                        message={datasetErrors.message}
                        fields={datasetErrors.fields}
                      />
                    ) : (
                      <>
                        <ICSTextInput
                          name="name"
                          control={control}
                          errors={errors}
                          isSubmitted={isSubmitted}
                        />
                        <ICSTextAreaInput
                          name="description"
                          control={control}
                          errors={errors}
                          isSubmitted={isSubmitted}
                        />
                      </>
                    )}
                  </Col>
                </Row>
                <ComponentBoxFooter>
                  <ICSButton
                    id="formSubmit"
                    width={140}
                    useSubmitBehavior={true}
                    type="success"
                    disabled={!isValid}
                  >
                    Save
                  </ICSButton>
                </ComponentBoxFooter>
              </FormGroup>
            </Form>
          </Col>
        </Row>
      </ModalBody>
    </ICSModal>
  )

  if (dynamic) {
    return (
      <div className="alert alert-info">
        <div>All query results will be used for dynamic dataset</div>
      </div>
    )
  }

  return (
    <>
      {resultJsonErrors?.message ? (
        <ICSErrorCard
          code={resultJsonErrors.code}
          message={resultJsonErrors.message}
          fields={resultJsonErrors.fields}
        />
      ) : (
        <>
          {createDataSetModal}
          <div className="well well-sm clearfix">
            <div className="btn-group pull-right">
              {extensionsToShow.length > 0 && (
                <label className="select-all">Select All</label>
              )}
              {extensionsToShow.map((extension, i: number) => (
                <button
                  key={`${extension}-${i}`}
                  className={`extension-button ${
                    extension.active ? 'active' : ''
                  }`}
                  onClick={() => updateSelectedForAll(extension)}
                >
                  {extension.name}
                </button>
              ))}
            </div>
            <div className="pull-right">
              <button
                className="glyphicons glyphicons-package"
                disabled={selectedTotal.count === 0}
                onClick={toggleCreateModal}
              >
                Create Static Data Set
              </button>
              <button className="glyphicons glyphicons-check" disabled>
                {selectedTotal.count} files ({getFileSize(selectedTotal.size)})
                selected
              </button>
              <ul uib-dropdown-menu="" className="dropdown-menu">
                <li>
                  <a>
                    <span className="glyphicons glyphicons-compressed"></span>{' '}
                    Create Download
                  </a>
                </li>
                <li role="separator" className="divider"></li>

                <li>
                  <a>
                    <span className="glyphicons glyphicons-restart"></span>{' '}
                    Export Selections
                  </a>
                </li>
                <li role="separator" className="divider"></li>
                <li>
                  <a>
                    <span className="glyphicons glyphicons-remove"></span> Clear
                    Selections
                  </a>
                </li>
                <li role="separator" className="divider"></li>
                <li>
                  <a>
                    <span className="glyphicons glyphicons-bin"></span> Delete
                    Selections
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="dataset-display">
            <ICSDataGrid
              dataSource={resultJson}
              columnOptions={[
                {
                  dataField: 'startDate',
                  caption: 'Start',
                  cellRender: renderDateTime
                },
                {
                  dataField: 'endDate',
                  caption: 'End',
                  cellRender: renderDateTime
                },
                { dataField: 'vehicle.name', caption: 'Vehicle' },
                { dataField: 'displayName', caption: 'Name' },
                { dataField: 'script.name', caption: 'Script' },
                {
                  dataField: 'versions',
                  caption: 'Data Type',
                  cellRender: renderDataType,
                }
              ]}
            />
          </div>
        </>
      )}
    </>
  )
}

export default DataSetsDisplay
